import React from 'react';
import { useParams } from 'react-router-dom';
import * as storeExt from '../store';
import { useTitle } from '../utils';
import { ProjectHeader } from '../components/ProjectHeader';
import lodashGroupBy from 'lodash/groupBy';
import lodashFindIndex from 'lodash/findIndex';
import lodashSortBy from 'lodash/sortBy';
import lodashJoin from 'lodash/join';
import lodashUniq from 'lodash/uniq';
import lodashFilter from 'lodash/filter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Spinner } from '@swegon-core/ui-components';
import { useHubConnection } from '../hubConnection';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { CheckboxField } from '../components';
import Select from 'react-select';
import { ChevronButton } from '../components';
import { ExportDocModal } from '../components/modals/ExportDocModal';

const {
    useGetProjectQuery,
    useGetConfigQuery,
    useGetQuoteDocumentsQuery,
    useDeleteQuoteDocumentMutation,
    useSubmitQuoteDocBatchJobV2Mutation,
    useGetProjectReadyQuery,
    useGetQuoteHasNonCategorizedDocQuery,
    documentsApi,
    projectsApi,
    mainActions,
} = storeExt;

const SortByDocCreatedDateDescending = (a, b) =>
    SortByDocCreatedDate(a, b, false);

const SortByDocCreatedDate = (a, b, ascending = true) => {
    if (a.createdAt < b.createdAt) {
        return ascending ? -1 : 1;
    }
    if (a.createdAt > b.createdAt) {
        return ascending ? 1 : -1;
    }
    return 0;
};

const GetMenuDisableValue = (menuEntries, value) => {
    const idx = lodashFindIndex(menuEntries, function (o) {
        return o.value === value;
    });
    if (idx >= 0) {
        return menuEntries[idx].disabled;
    }
    return true;
};

const GetDocGroupDescription = (docType) => {
    var result = '';

    switch (docType) {
        case 0:
            result = 'Sales Submittal';
            break;
        case 1:
            result = 'Solidworks Asset';
            break;
        case 2:
            result = 'Data Sheets';
            break;
        case 3:
            result = 'Engineering Document';
            break;
        case 4:
            result = 'Shop Drawings';
            break;
        case 5:
            result = 'Quote Documents';
            break;
        default:
            result = 'Undocumented category'
            break;
    }
    return result;
}

export const Document = () => {
    const dispatch = useDispatch();
    const { projectId } = useParams();
    const { data: project } = useGetProjectQuery(projectId);
    const { data: documents, isLoading: isDocLoading } =
        useGetQuoteDocumentsQuery(projectId);
    const { data: config } = useGetConfigQuery();
    const { data: stillprinting } =
        storeExt.useGetQuoteHasJobInProgressQuery(projectId);
    const { data: quoteIsReady } = useGetProjectReadyQuery(projectId);
    const [submitBatchBuildJobV2] = useSubmitQuoteDocBatchJobV2Mutation();
    const {
        data: hasNonCategorizedDoc,
        isLoading: isCheckingDocumentCategory,
        isError: categoryDocCheckFailed,
    } = useGetQuoteHasNonCategorizedDocQuery(projectId);
    const [showExportModal, setExportModalOpen] = React.useState(false);
    const [selectedSubmittal, setSelectedSubmittal] = React.useState(true); // Submittal
    const [selectedDataSheets, setSelectedDataSheets] = React.useState(false);  // Data Sheets
    const [selectedCalcSummary, setSelectedCalcSummary] = React.useState(false);    // Calculation Summary
    const [selectedShopPackage, setSelectedShopPackage] = React.useState(true); // Shop Drawings
    const [selectedBOMSummary, setSelectedBOMSummary] = React.useState(false);  // BOM
    const [selectedSolidworks, setSelectdSolidworks] = React.useState(false);   // Solidworks
    const [submittalSelectedTags, setSubmittalSelectedTags] = React.useState(
        [],
    );  // Submittal Tags
    const [dataSheetSelectedTags, setDataSheetSelectedTags] = React.useState(
        [],
    );  // Data Sheets Tags
    const [calcSummarySelectedTags, setCalcSummarySelectedTags] =
        React.useState([]); // Calculation Summary Tags
    const [shopPackageSelectedTags, setShopPackageSelectedTags] =
        React.useState([]); // Shop Drawings Tags
    const [bomSelectedTags, setBomSelectedTags] =
        React.useState([]); // BOM Tags
    const [solidworksSelectedTags, setSolidworksSelectedTags] =
        React.useState([]); // Solidworks Tags
    const defaultSortedTagList = React.useMemo(() => {
        return project?.tags
            .map((tag) => tag.value)
            .reduce(function (acc, curr) {
                if (!acc.includes(curr)) acc.push(`"${curr}"`);
                return acc;
            }, [])
            .sort();
    }, [project?.tags]);

    const needExport = React.useMemo(() => {
        return (
            selectedSubmittal ||
            selectedDataSheets ||
            selectedCalcSummary ||
            selectedShopPackage ||
            selectedBOMSummary ||
            selectedSolidworks
        );
    }, [
        selectedSubmittal,
        selectedDataSheets,
        selectedCalcSummary,
        selectedShopPackage,
        selectedBOMSummary,
        selectedSolidworks,
    ]);

    const selectedOutputTypes = React.useMemo(() => {
        return {
            submittal: selectedSubmittal,
            dataSheet: selectedDataSheets,
            calcSummary: selectedCalcSummary,
            shopPackage: selectedShopPackage,
            bomSumamry: selectedBOMSummary,
            solidworks: selectedSolidworks,
        };
    }, [
        selectedSubmittal,
        selectedDataSheets,
        selectedCalcSummary,
        selectedShopPackage,
        selectedBOMSummary,
        selectedSolidworks,
    ]);
    const selectedOutputs = React.useMemo(() => {
        const numOutputs = Object.entries(selectedOutputTypes)
            .map(([k, v]) => {
                return v ? 1 : 0;
            })
            .reduce((acc, v) => {
                return acc + v;
            }, 0);
        return numOutputs;
    }, [selectedOutputTypes]);

    const submitBatchBuildJobFn = React.useMemo(() => {
        return submitBatchBuildJobV2;
    }, [submitBatchBuildJobV2]);

    useTitle(React.useMemo(() => project?.name ?? 'Project', [project]));

    const hubConnection = useHubConnection();
    React.useEffect(() => {
        const handleReceiveItemDocMessage = (jobId, itemId, projectId) => {
            dispatch(
                documentsApi.util.prefetch('getProductDocuments', itemId, {
                    force: true,
                }),
            );
            dispatch(
                documentsApi.util.prefetch(
                    'getProductHasJobInProgress',
                    itemId,
                    { force: true },
                ),
            );
            dispatch(
                projectsApi.util.prefetch('getProjectReady', projectId, {
                    force: true,
                }),
            );
        };
        const sendItemDocAck = (connection, jobId, itemId, isWorkflowJob) => {
            connection.invoke(
                'ItemDocAcknowledge',
                jobId,
                itemId,
                isWorkflowJob,
            );
        };
        const handleReceiveQuoteDocMessage = (jobId, quoteId) => {
            dispatch(
                documentsApi.util.prefetch('getQuoteDocuments', quoteId, {
                    force: true,
                }),
            );
            dispatch(
                documentsApi.util.prefetch(
                    'getQuoteHasJobInProgress',
                    quoteId,
                    { force: true },
                ),
            );
            dispatch(
                projectsApi.util.prefetch('getProjectReady', quoteId, {
                    force: true,
                }),
            );
        };
        const sendQuoteDocAck = (connection, jobId, quoteId) => {
            connection.invoke('QuoteDocAcknowledge', jobId, quoteId);
        };

        if (hubConnection && config?.signalR) {
            hubConnection.then((connection) => {
                connection.on(
                    config.signalR.ItemDoc ?? 'ItemMessage',
                    (jobId, itemId, isWorkflowJob, projectId) => {
                        handleReceiveItemDocMessage(jobId, itemId, projectId);
                        sendItemDocAck(
                            connection,
                            jobId,
                            itemId,
                            isWorkflowJob,
                        );
                    },
                );
                connection.on(
                    config.signalR.QuoteDoc ?? 'QuoteMessage',
                    (jobId, quoteId) => {
                        handleReceiveQuoteDocMessage(jobId, quoteId);
                        sendQuoteDocAck(connection, jobId, quoteId);
                    },
                );
            });
        }

        // Clean up
        return () => {
            if (hubConnection && config?.signalR) {
                hubConnection.then((connection) => {
                    connection.off(
                        config.signalR.ItemDoc ?? 'ItemMessage',
                        handleReceiveItemDocMessage,
                    );
                    connection.off(
                        config.signalR.QuoteDoc ?? 'QuoteMessage',
                        handleReceiveQuoteDocMessage,
                    );
                });
            }
        };
    }, [hubConnection, config, dispatch]);

    const getCheckedByValue = React.useCallback(
        (value) => {
            let result;
            switch (value) {
                case 'CalculationSummary':
                    result = selectedCalcSummary;
                    break;
                case 'DataSheet':
                    result = selectedDataSheets;
                    break;
                case 'ShopPackage':
                    result = selectedShopPackage;
                    break;
                case 'Submittal':
                    result = selectedSubmittal;
                    break;
                case 'BOMSummary':
                    result = selectedBOMSummary;
                    break;
                case 'Solidworks':
                    result = selectedSolidworks;
                    break;
                default:
                    result = null;
            }
            return result;
        },
        [
            selectedCalcSummary,
            selectedDataSheets,
            selectedShopPackage,
            selectedSubmittal,
            selectedBOMSummary,
            selectedSolidworks,
        ],
    );

    const exportMenu = React.useMemo(() => {
        if (!config?.exportMenu) return [];
        const filteredEntries = lodashFilter(config?.exportMenu, [ 'included', true]);
        // const temp = lodashSortBy(config?.exportMenu, ['ordinal']);
        const temp = lodashSortBy(filteredEntries, ['ordinal']);

        const result = temp.map((item, index) => ({
            label: item.label,
            value: item.value,
            description: item.description,
            hasTagSelection: item.hasTagSelection,
            // disabled: GetMenuDisableValue(config?.exportMenu, item.value),
            disabled: GetMenuDisableValue(temp, item.value),
        }));
        return result;
    }, [config?.exportMenu]);

    const sortAndFormatTagList = (tagList) => {
        const result = tagList.sort().reduce(function (acc, curr) {
            acc.push(`"${curr}"`);
            return acc;
        }, []);
        return result;
    };

    const exportDocument = async () => {
        if (selectedOutputs === 0) {
            setExportModalOpen(false);
            return;
        }

        const finalSubmittalTags = lodashJoin(
            submittalSelectedTags.length > 0
                ? lodashUniq(sortAndFormatTagList(submittalSelectedTags))
                : defaultSortedTagList,
            ',',
        );
        const finalCalcSummaryTags = lodashJoin(
            calcSummarySelectedTags.length > 0
                ? lodashUniq(sortAndFormatTagList(calcSummarySelectedTags))
                : defaultSortedTagList,
            ',',
        );
        const finalDataSheetTags = lodashJoin(
            dataSheetSelectedTags.length > 0
                ? lodashUniq(sortAndFormatTagList(dataSheetSelectedTags))
                : defaultSortedTagList,
            ',',
        );
        const finalShopPackageTags = lodashJoin(
            shopPackageSelectedTags.length > 0
                ? lodashUniq(sortAndFormatTagList(shopPackageSelectedTags))
                : defaultSortedTagList,
            ',',
        );
        const finalSolidworksTags = lodashJoin(
            solidworksSelectedTags.length > 0
                ? lodashUniq(sortAndFormatTagList(solidworksSelectedTags))
                : defaultSortedTagList,
            ',',
        );

        /*        if (selectedOutputs === 1) {
            let printDocType;
            if (selectedSubmittal) {
                printDocType = quoteDocuments.SalesSubmittals;
            } else if (selectedDataSheets) {
                printDocType = quoteDocuments.SalesSubmittals;
            } else if (selectedCalcSummary) {
                printDocType = quoteDocuments.SalesSubmittals;
            } else {
                printDocType = quoteDocuments.ShopPackage;
            }
            dispatch(
                mainActions.showWaitingModal({
                    isSimple: false,
                    msg: "Exporting Project's Sales Submittals...\nPlease wait as it will take a while.",
                }),
            );
            setExportModalOpen(false);
            try { */
        /*
                await buildDocument({
                    id: project.id,
                    docType: quoteDocuments.SalesSubmittals,
                }).unwrap();
                */
        /*                await submitBuildJob({
                    id: project.id,
                    docType: printDocType,
                }).unwrap();
            } catch (err) {
                console.log(err);
            } finally {
                dispatch(mainActions.hideWaitingModal());
                dispatch(documentsApi.util.prefetch('getQuoteHasJobInProgress',
                    projectId, { force: true, }));
            }
        } */
        dispatch(
            mainActions.showWaitingModal({
                isSimple: false,
                msg: "Exporting Project's Sales Submittals...\nPlease wait as it will take a while.",
            }),
        );
        setExportModalOpen(false);
        try {
            var requestBody = {
                printSubmittal: selectedSubmittal,
                submittalPrintTags: selectedSubmittal
                    ? `[${finalSubmittalTags}]`
                    : '',
                printSolidworks: selectedSolidworks,
                solidworksPrintTags: selectedSolidworks
                    ? `[${finalSolidworksTags}]`
                    : '',
                printDataSheet: selectedDataSheets,
                dataSheetPrintTags: selectedDataSheets
                    ? `[${finalDataSheetTags}]`
                    : '',
                printCalcSummary: selectedCalcSummary,
                calcSummaryPrintTags: selectedCalcSummary
                    ? `[${finalCalcSummaryTags}]`
                    : '',
                printShopPackage: selectedShopPackage,
                shopPackagePrintTags: selectedShopPackage
                    ? `[${finalShopPackageTags}]`
                    : '',
                printBOMSummary: selectedBOMSummary,
            };
            await submitBatchBuildJobFn({
                id: project.id,
                req: requestBody,
            }).unwrap();
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(mainActions.hideWaitingModal());
            dispatch(
                documentsApi.util.prefetch(
                    'getQuoteHasJobInProgress',
                    projectId,
                    { force: true },
                ),
            );
            dispatch(
                projectsApi.util.prefetch('getProjectReady', projectId, {
                    force: true,
                }),
            );
        }
    };

    const groupedOptions = React.useMemo(() => {
        const equips = project?.tags
            .map((tag) => tag.equipmentType)
            .reduce(function (acc, curr) {
                if (!acc.includes(curr)) acc.push(curr);
                return acc;
            }, [])
            .sort();
        const options = project?.tags
            .map((tag) => ({
                value: tag.id,
                label: tag.value,
                equipmentType: tag.equipmentType,
            }))
            .sort((item) => item.label);
        return equips !== undefined
            ? equips.map((equip) => {
                  return {
                      label: equip,
                      options: options.filter(
                          (option) => option.equipmentType === equip,
                      ),
                  };
              })
            : null;
    }, [project?.tags]);

    const onSubmittalTagsChange = (newValue, actionMeta) => {
        let setValue;
        switch (actionMeta.action) {
            case 'clear':
                setValue = [];
                break;
            case 'remove-value': {
                const idx = submittalSelectedTags.indexOf(
                    actionMeta.removedValue.label,
                );
                if (idx >= 0) {
                    setValue = submittalSelectedTags.toSpliced(idx, 1);
                }
                break;
            }
            case 'select-option':
                setValue = [
                    ...submittalSelectedTags,
                    newValue[newValue.length - 1].label,
                ];
                break;
            default:
                return;
        }
        setSubmittalSelectedTags(setValue);
    };

    const onShopPackageTagsChange = (newValue, actionMeta) => {
        let setValue;
        switch (actionMeta.action) {
            case 'clear':
                setValue = [];
                break;
            case 'remove-value': {
                const idx = shopPackageSelectedTags.indexOf(
                    actionMeta.removedValue.label,
                );
                if (idx >= 0) {
                    setValue = shopPackageSelectedTags.toSpliced(idx, 1);
                }
                break;
            }
            case 'select-option':
                setValue = [
                    ...shopPackageSelectedTags,
                    newValue[newValue.length - 1].label,
                ];
                break;
            default:
                return;
        }
        setShopPackageSelectedTags(setValue);
    };

    const onDataSheetTagsChange = (newValue, actionMeta) => {
        let setValue;
        switch (actionMeta.action) {
            case 'clear':
                setValue = [];
                break;
            case 'remove-value': {
                const idx = dataSheetSelectedTags.indexOf(
                    actionMeta.removedValue.label,
                );
                if (idx >= 0) {
                    setValue = dataSheetSelectedTags.toSpliced(idx, 1);
                }
                break;
            }
            case 'select-option':
                setValue = [
                    ...dataSheetSelectedTags,
                    newValue[newValue.length - 1].label,
                ];
                break;
            default:
                return;
        }
        setDataSheetSelectedTags(setValue);
    };

    const onCalcSummaryTagsChange = (newValue, actionMeta) => {
        let setValue;
        switch (actionMeta.action) {
            case 'clear':
                setValue = [];
                break;
            case 'remove-value': {
                const idx = calcSummarySelectedTags.indexOf(
                    actionMeta.removedValue.label,
                );
                if (idx >= 0) {
                    setValue = calcSummarySelectedTags.toSpliced(idx, 1);
                }
                break;
            }
            case 'select-option':
                setValue = [
                    ...calcSummarySelectedTags,
                    newValue[newValue.length - 1].label,
                ];
                break;
            default:
                return;
        }
        setCalcSummarySelectedTags(setValue);
    };

    const onSolidworksTagsChange = (newValue, actionMeta) => {
        let setValue;
        switch (actionMeta.action) {
            case 'clear':
                setValue = [];
                break;
            case 'remove-value': {
                const idx = solidworksSelectedTags.indexOf(
                    actionMeta.removedValue.label,
                );
                if (idx >= 0) {
                    setValue = solidworksSelectedTags.toSpliced(idx, 1);
                }
                break;
            }
            case 'select-option':
                setValue = [
                    ...solidworksSelectedTags,
                    newValue[newValue.length - 1].label,
                ];
                break;
            default:
                return;
        }
        setSolidworksSelectedTags(setValue);
    };

    const onBOMTagsChange = (newValue, actionMeta) => {
        let setValue;
        switch (actionMeta.action) {
            case 'clear':
                setValue = [];
                break;
            case 'remove-value': {
                const idx = bomSelectedTags.indexOf(
                    actionMeta.removedValue.label,
                );
                if (idx >= 0) {
                    setValue = bomSelectedTags.toSpliced(idx, 1);
                }
                break;
            }
            case 'select-option':
                setValue = [
                    ...bomSelectedTags,
                    newValue[newValue.length - 1].label,
                ];
                break;
            default:
                return;
        }
        setBomSelectedTags(setValue);
    };

    const getOnCheckboxChangeHandler = (value) => {
        switch (value) {
            case 'Submittal':
                setSelectedSubmittal(!selectedSubmittal);
                break;
            case 'Solidworks':
                setSelectdSolidworks(!selectedSolidworks);
                break;
            case 'DataSheet':
                setSelectedDataSheets(!selectedDataSheets);
                break;
            case 'CalculationSummary':
                setSelectedCalcSummary(!selectedCalcSummary);
                break;
            case 'ShopPackage':
                setSelectedShopPackage(!selectedShopPackage);
                break;
            case 'BOMSummary':
                setSelectedBOMSummary(!selectedBOMSummary);
                break;
            default:
                break;
        }
    };

    const getTagChangeHandler = (value) => {
        var handler = null;
        switch (value) {
            case 'Submittal':
                handler = onSubmittalTagsChange;
                break;
            case 'Solidworks':
                handler = onSolidworksTagsChange;
                break;
            case 'DataSheet':
                handler = onDataSheetTagsChange;
                break;
            case 'CalculationSummary':
                handler = onCalcSummaryTagsChange;
                break;
            case 'ShopPackage':
                handler = onShopPackageTagsChange;
                break;
            case 'BOMSummary':
                handler = onBOMTagsChange;
                break;
            default:
                break;
        }
        return handler;
    };

    const content = (
        <React.Fragment>
            <h1>Export</h1>
            <div className="my-6 px-4 border border-black-20">
                {exportMenu.map((item, index) => {
                    const css =
                        index === 0
                            ? classNames(
                                  'pb-4 pt-2',
                                  item.disabled ? 'text-black-20' : '',
                              )
                            : classNames(
                                  'py-4 border-t border-black-20',
                                  item.disabled ? 'text-black-20' : '',
                              );
                    var corrOnChange = getTagChangeHandler(item.value);

                    return (
                        <div key={index}>
                            <div
                                className={css}
                                key={item.value}
                            >
                                <div className="flex items-center gap-2">
                                    <CheckboxField
                                        value={item.value}
                                        title={item.label}
                                        checked={getCheckedByValue(item.value)}
                                        onChange={() => getOnCheckboxChangeHandler(item.value)}
                                        disabled={item.disabled}
                                    />
                                    <span>{item.description}</span>
                                </div>
                            </div>
                            <div className="mt-4 mb-4">
                                {/*item.value !== 'BOMSummary' && (
                                    <Select
                                        placeholder="All tags"
                                        isMulti
                                        options={groupedOptions}
                                        isDisabled={item.disabled}
                                        className="basic-multi-select"
                                        classNamePrefix="select"
                                        name={item.value + '-tags'}
                                        onChange={corrOnChange}
                                        menuPlacement={
                                            index !== exportMenu.length - 1
                                                ? 'bottom'
                                                : 'top'
                                        }
                                    />
                                )*/}
                                { item.hasTagSelection && (
                                    <Select
                                        placeholder="All tags"
                                        isMulti
                                        options={groupedOptions}
                                        isDisabled={item.disabled}
                                        className="basic-multi-select"
                                        classNamePrefix="select"
                                        name={item.value + '-tags'}
                                        onChange={corrOnChange}
                                        blurInputOnSelect={true}
                                        menuPlacement={
                                            index !== exportMenu.length - 1
                                                ? 'bottom'
                                                : 'top'
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </React.Fragment>
    );

    return (
        <div className="">
            <ProjectHeader
                parentPath="documents"
                setExportState={setExportModalOpen}
                headerOpsDisable={!quoteIsReady}
            />
            { isCheckingDocumentCategory ? (
                <div>Checking whether project has categorized document...</div>
              ) : categoryDocCheckFailed ? (
                <div>Could not determine whether project has categorized document.</div>
              ) : hasNonCategorizedDoc === true ? (
                <DocumentContent
                    project={project}
                    isLoading={isDocLoading}
                    documents={documents}
                    hasJobInProgress={stillprinting}
                />
              ) : hasNonCategorizedDoc === false ? (
                <CategorizedDocumentContent
                    project={project}
                    isLoading={isDocLoading}
                    documents={documents}
                    hasJobInProgress={stillprinting}
                />
              ): null}
            <ExportDocModal
                show={showExportModal}
                onClose={() => setExportModalOpen(false)}
                onExport={exportDocument}
                needExport={needExport}
            >
                {content}
            </ExportDocModal>
        </div>
    );
};

const DocumentContent = (props) => {
    const { project, isLoading, documents, hasJobInProgress } = props;
    const hasDocumetns = documents?.length > 0;

    return (
        <div className="bg-white border border-black-20 px-4 py-4">
            <div className="border border-black-20 px-4 py-4 divide-y divide-black-20">
                <div className="text-2xl font-bold mb-2">Documents</div>
                <table className="tags-table">
                    <DocumentHeader />
                    {isLoading ? (
                        <tbody>
                            <tr>
                                <td className="pl-1 pr-1">&nbsp;</td>
                                <td colSpan="4">Loading documents...</td>
                            </tr>
                            <tr>
                                <td className="pl-1 pr-1">&nbsp;</td>
                                <td colSpan="4">
                                    <Spinner color="green" />
                                </td>
                            </tr>
                        </tbody>
                    ) : hasDocumetns ? (
                        <DocumentBody
                            project={project}
                            documents={documents}
                            hasJobInProgress={hasJobInProgress}
                        />
                    ) : (
                        <tbody>
                            {hasJobInProgress ? (
                                <tr key={'Spinning'}>
                                    <td
                                        align="center"
                                        colSpan="4"
                                    >
                                        <Spinner color="green" />
                                    </td>
                                </tr>
                            ) : null}
                            <tr>
                                <td className="pl-1 pr-1">&nbsp;</td>
                                <td colSpan="4">
                                    Currently no documents to show here
                                </td>
                            </tr>
                        </tbody>
                    )}
                </table>
            </div>
        </div>
    );
};

const DocumentHeader = () => {
    return (
        <thead className="text-black-30 border-t-2 border-b-2 border-black-20">
            <tr>
                <th className="pl-1 pr-1 w-1">{'\xa0'}</th>
                <th className="pl-1 pr-4 w-3/5">FileName</th>
                <th className="px-4 w-1 w-1/5">Revison</th>
                <th className="pl-4 pr-1">Date Created</th>
                <th className="pl-1 pr-1 w-1">{'\xa0'}</th>
            </tr>
        </thead>
    );
};

const DocRow = (props) => {
    const { project, document, firstRow, headerRow, status, onClick, categorizedDocs = false } = props;
    const [deleteQuoteDoc] = useDeleteQuoteDocumentMutation();
    const firstCol =
        firstRow && headerRow ? (
            <ChevronButton
                open={status}
                onClick={() => onClick(!status)}
            />
        ) : !firstRow ? (
            <FontAwesomeIcon
                icon="file-solid fa-triangle-exclamation"
                style={{ color: '#d21414' }}
            />
        ) : null;

    if (!project) return null;

    const downloadUrl = categorizedDocs || document.auxInfo !== null
        ? `/api/documents/quote/${project.id}/doc/categorized/${document.id}`
        : `/api/documents/quote/${project.id}/doc/${document.docId}`

    const handleDeleteDoc = async (doc) => {
        try {
            await deleteQuoteDoc({
                id: project.id,
                docId: doc.id,
                seqId: doc.sequenceId,
            }).unwrap();
        } catch (err) {
            console.log(err);
        }
    };

    return (
        <tr
            className="border-b border-t border-black-20 text-base"
            key={document.id}
        >
            <td className="pl-1 pr-1">{firstCol}</td>
            <td className="pl-1 pr-4">
                <a
                    className="text-green-primary hover:underline hover:curosr-pointer"
                    href={downloadUrl}
                    download={document.originalDocName}
                >
                    {document.originalDocName}
                </a>
            </td>
            <td className="px-4">{document.revision}</td>
            <td className="px-4">
                {new Date(document.createdAt).toLocaleString('en-US')}
            </td>
            <td>
                <button onClick={() => handleDeleteDoc(document)}>
                    <FontAwesomeIcon
                        className="mr-6"
                        icon="fa-solid fa-trash-can"
                    />
                </button>
            </td>
        </tr>
    );
};

const DocRows = (props) => {
    const { project, docs, categorizedDocs = false } = props;

    const [open, setOpen] = React.useState(false);

    if (docs.length === 1) {
        return (
            <DocRow
                document={docs[0]}
                project={project}
                firstRow={true}
                headerRow={false}
                categorizedDocs={categorizedDocs}
            />
        );
    }

    const orderedDocs = docs.sort((a, b) =>
        SortByDocCreatedDateDescending(a, b),
    );

    return (
        <React.Fragment>
            <DocRow
                key={orderedDocs[0].id}
                project={project}
                document={orderedDocs[0]}
                firstRow={true}
                headerRow={true}
                status={open}
                onClick={setOpen}
                categorizedDocs={categorizedDocs}
            />

            {open
                ? orderedDocs.map((doc, index) => {
                      if (index !== 0) {
                          return (
                              <DocRow
                                  key={doc.id}
                                  project={project}
                                  document={doc}
                                  firstRow={false}
                                  headerRow={false}
                                  categorizedDocs={categorizedDocs}
                              />
                          );
                      }
                      return null;
                  })
                : null}
        </React.Fragment>
    );
};

const DocumentBody = (props) => {
    const { project, documents, hasJobInProgress } = props;

    const groups = lodashGroupBy(documents ?? [], 'originalDocName');

    return (
        <tbody>
            {hasJobInProgress ? (
                <tr key={'Spinning'}>
                    <td
                        align="center"
                        colSpan="5"
                    >
                        <Spinner color="green" />
                    </td>
                </tr>
            ) : null}
            {Object.entries(groups).map(([name, docs]) => (
                <DocRows
                    key={name}
                    project={project}
                    docs={docs}
                />
            ))}
        </tbody>
    );
};

// Categoriezed Documents Presentation
const CategorizedDocumentContent = (props) => {
    const { project, isLoading, documents, hasJobInProgress } = props;
    const hasDocuments = documents?.length > 0;

    return (
        <div className="bg-white border border-black-20 px-4 py-4">
            <div className="border border-black-20 px-4 py-4">
                    {isLoading ? (
                        <table className="tags-table">
                        <tbody>
                            <tr>
                                <td className="pl-1 pr-1">&nbsp;</td>
                                <td colSpan="4">Loading documents...</td>
                            </tr>
                            <tr>
                                <td className="pl-1 pr-1">&nbsp;</td>
                                <td colSpan="4">
                                    <Spinner color="green" />
                                </td>
                            </tr>
                        </tbody>
                        </table>
                    ) : hasDocuments ? (
                        <CategorizedDocumentBody
                            project={project}
                            documents={documents}
                            hasJobInProgress={hasJobInProgress}
                        />
                    ) : (
                        <table className="tags-table">
                            <DocumentHeader />
                            <tbody>
                                {hasJobInProgress ? (
                                    <tr key={'Spinning'}>
                                        <td align="center" colSpan="4">
                                            <Spinner color="green" />
                                        </td>
                                    </tr>
                                ) : null}
                                <tr>
                                    <td className="pl-1 pr-1">&nbsp;</td>
                                    <td colSpan="4">
                                        Currently no documents to show here
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    )}
            </div>
        </div>
    );
};

const CategoriezedDocGroups = (props) => {
    const { docType, docs, project} = props;
    const docsArray = (docs ?? []).map(({doc}) => doc);
    const groupedDoc = lodashGroupBy(docsArray ?? [], 'originalDocName');

    return (
        <div className="mb-6">
            <div className="text-2xl font-bold mb-2">{GetDocGroupDescription(docType)}</div>
            <table className="tags-table">
            <DocumentHeader />
            <tbody>
            {Object.entries(groupedDoc).map(([name, docs]) => (
                <DocRows
                    key={name}
                    project={project}
                    docs={docs}
                    categorizedDocs={true}
                />
            ))}
            </tbody>
            </table>
        </div>
    );
}

const CategorizedDocumentBody = (props) => {
    const { project, documents, hasJobInProgress } = props;

    const groupedInfoDoc = (documents ?? []).map((doc) => ({
        docType: doc.auxInfo?.docType ?? 0,
        doc
    }));

    const docGroups = lodashGroupBy(groupedInfoDoc ?? [], 'docType');

    return (
        <>
            {hasJobInProgress ? (
                <tr key={'Spinning'}>
                    <td align="center" colSpan="5">
                        <Spinner color="green" />
                    </td>
                </tr>
            ) : null}
            {Object.entries(docGroups).map(([docType, docs]) => (
                <CategoriezedDocGroups
                    key={docType}
                    project={project}
                    docType={Number(docType)}
                    docs={docs}
                />
            ))}
        </>
    );
};
